export const AWB_DIGITS = 11;
export const AWB_PREFIX_DIGITS = 3;
const AWB_GROUP_BREAK = 7;

export const formatAwb = (awb: string, defaultValue?: string) => {
  if (!awb) return defaultValue || '';

  // Remove all dashes and trim the string
  const cleanedAwb = awb.replace(/-/g, '').trim();

  return cleanedAwb?.length > 3 ? `${cleanedAwb?.slice(0, 3)}-${cleanedAwb.slice(3)}` : cleanedAwb;
};

export const removeDash = (value?: string) => value?.replace(/[\W\s._-]+/g, '') || '';

export const addDash = (value: string) => value?.slice(0, 3) + '-' + value?.slice(3);

/** "020-4471 9902" → "02044719902", the bare-digit form `Quote.awb_number` stores. Null unless exactly 11 digits remain. */
export const normalizeAwb = (value?: string) => {
  const digits = (value ?? '').replace(/[^0-9]/g, '');
  return digits.length === AWB_DIGITS ? digits : null;
};

/**
 * "235-81953745" → { prefix: "235", serial: "81953745" }: the halves carrier APIs take separately. Null unless exactly
 * 11 digits remain.
 */
export const splitAwb = (value?: string) => {
  const digits = normalizeAwb(value);
  if (!digits) return null;
  return { prefix: digits.slice(0, AWB_PREFIX_DIGITS), serial: digits.slice(AWB_PREFIX_DIGITS) };
};

/**
 * IATA check digit: the last digit of the serial is the preceding 7 modulo 7. Used to RANK candidates, never to reject
 * them — a carrier that mistypes its own AWB still means the shipment it names, and the quote lookup will say so.
 */
export const hasValidCheckDigit = (value?: string) => {
  const normalized = normalizeAwb(value);
  if (!normalized) return false;
  const serial = normalized.slice(AWB_PREFIX_DIGITS);
  return Number(serial.slice(0, 7)) % 7 === Number(serial.slice(7));
};

/**
 * "02044719902" → "020-4471 9902": the grouped form carriers print on a booking confirmation. Distinct from
 * `formatAwb`, which emits the prefix-dash-only form used elsewhere in the app. Returns the input unchanged when it is
 * not an 11-digit AWB.
 */
export const formatPrintedAwb = (value?: string) => {
  const digits = normalizeAwb(value);
  if (!digits) return value ?? null;
  return `${digits.slice(0, AWB_PREFIX_DIGITS)}-${digits.slice(AWB_PREFIX_DIGITS, AWB_GROUP_BREAK)} ${digits.slice(AWB_GROUP_BREAK)}`;
};
